import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import DeveloperNavigation, { DEV_ADMIN_EMAILS } from '../components/DeveloperNavigation';
import { getTmsApiUrl } from '../utils/apiUrls';

const BACKEND_URL = getTmsApiUrl();

const ACTING_STATUS_LABELS = {
  keep_keyflo: 'Keeping KeyFlo values…',
  use_skyslope: 'Keeping SkySlope values…',
  dismissed: 'Resolving discrepancy…',
};

const COLUMN_ORDER = [
  'id',
  'user_email',
  'user_id',
  'contract_id',
  'listing_id',
  'property_address',
  'field_name',
  'skyslope_value',
  'keyflo_value',
  'resolution',
  'resolved_at',
  'resolved_by',
  'sync_job_id',
  'created_at',
];

function DeveloperSkySlopeDiscrepancies() {
  const { user, isAuthenticated, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();

  const [pageSize, setPageSize] = useState(20);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [offset, setOffset] = useState(0);
  const [actingId, setActingId] = useState(null);

  const [userEmail, setUserEmail] = useState('');
  const [contractId, setContractId] = useState('');
  const [listingId, setListingId] = useState('');
  const [fieldName, setFieldName] = useState('');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [openOnly, setOpenOnly] = useState(true);

  const isKeyFloUser = useMemo(() => {
    const email = user?.email || '';
    return DEV_ADMIN_EMAILS.includes(email);
  }, [user]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!isKeyFloUser) {
      navigate('/');
    }
  }, [isAuthenticated, isKeyFloUser, navigate]);

  const formatDateTime = (ts) => {
    try {
      return ts ? new Date(ts).toLocaleString() : '';
    } catch (e) {
      return ts || '';
    }
  };

  const buildParams = (resetOffset = true, filters = {}) => {
    const nextEmail = Object.prototype.hasOwnProperty.call(filters, 'userEmail')
      ? filters.userEmail : userEmail;
    const nextContractId = Object.prototype.hasOwnProperty.call(filters, 'contractId')
      ? filters.contractId : contractId;
    const nextListingId = Object.prototype.hasOwnProperty.call(filters, 'listingId')
      ? filters.listingId : listingId;
    const nextFieldName = Object.prototype.hasOwnProperty.call(filters, 'fieldName')
      ? filters.fieldName : fieldName;
    const nextDateStart = Object.prototype.hasOwnProperty.call(filters, 'dateStart')
      ? filters.dateStart : dateStart;
    const nextDateEnd = Object.prototype.hasOwnProperty.call(filters, 'dateEnd')
      ? filters.dateEnd : dateEnd;
    const params = {
      limit: pageSize,
      offset: resetOffset ? 0 : offset,
    };
    if (nextEmail) params.user_email = nextEmail;
    if (nextContractId) params.contract_id = nextContractId;
    if (nextListingId) params.listing_id = nextListingId;
    if (nextFieldName) params.field_name = nextFieldName;
    if (nextDateStart) params.start_date = nextDateStart;
    if (nextDateEnd) params.end_date = nextDateEnd;
    if (openOnly) params.status = 'open';
    return params;
  };

  const fetchRows = async (reset = true, filters = {}) => {
    if (!isAuthenticated || !isKeyFloUser) return;
    try {
      setLoading(true);
      setError('');
      const params = buildParams(reset, filters);
      const response = await axios.get(`${BACKEND_URL}/inbound-sync/discrepancies`, { params });
      if (response.data?.status !== 'success') {
        throw new Error(response.data?.error || 'Failed to fetch discrepancies');
      }
      const next = response.data.discrepancies || [];
      setRows(reset ? next : [...rows, ...next]);
      setOffset((reset ? 0 : offset) + next.length);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load discrepancies');
    } finally {
      setLoading(false);
    }
  };

  const resolveRow = async (row, action, label) => {
    if (row.resolution || actingId) return;
    const confirmText = action === 'dismissed'
      ? `Resolve discrepancy #${row.id} without changing KeyFlo values?\n\n${row.field_name}`
      : `${label} for discrepancy #${row.id}?\n\n${row.field_name}`;
    if (!window.confirm(confirmText)) return;
    setActingId(`${row.id}:${action}`);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/inbound-sync/discrepancies/${row.id}/resolve`, {
        action,
        resolved_by: user?.email || 'dev_portal',
      });
      setRows((prev) =>
        prev.map((item) =>
          item.id === row.id
            ? {
                ...item,
                resolution: action,
                resolved_at: new Date().toISOString(),
                resolved_by: user?.email || 'dev_portal',
              }
            : item
        )
      );
      if (data?.skyslope_not_updated && data?.message) {
        alert(data.message);
      }
    } catch (e) {
      alert(`Failed to ${label.toLowerCase()}: ${e.response?.data?.error || e.message}`);
    } finally {
      setActingId(null);
    }
  };

  useEffect(() => {
    if (isAuthenticated && isKeyFloUser) fetchRows(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isKeyFloUser, pageSize, openOnly]);

  if (!isAuthenticated) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column' }}>
        <p>Please log in</p>
        <button onClick={() => loginWithRedirect({
          authorizationParams: { prompt: 'login' },
        })}>Log in</button>
      </div>
    );
  }

  if (!isKeyFloUser) {
    return (
      <div style={{ padding: '24px' }}>
        <h2>Access Denied</h2>
        <p>This page is restricted to KeyFlo team members.</p>
      </div>
    );
  }

  const isActing = !!actingId;
  const actingAction = actingId ? actingId.slice(String(actingId).indexOf(':') + 1) : '';
  const actingStatus = ACTING_STATUS_LABELS[actingAction] || 'Processing…';

  return (
    <>
    {isActing && (
      <div
        role="status"
        aria-live="polite"
        aria-busy="true"
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(251, 247, 244, 0.78)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 3000,
          cursor: 'wait',
        }}
      >
        <img
          src="/loading_animation.gif"
          alt=""
          style={{ width: 50, height: 50, display: 'block' }}
        />
        <div style={{ marginTop: 12, color: '#334155', fontSize: '1rem' }}>{actingStatus}</div>
      </div>
    )}
    <div
      style={{ padding: '24px', pointerEvents: isActing ? 'none' : 'auto' }}
      aria-busy={isActing}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h2>SkySlope Discrepancies</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
          <button className="back-button" onClick={() => navigate('/')} disabled={isActing}>← Back to Contracts</button>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', userSelect: 'none' }}>
            <input
              type="checkbox"
              checked={openOnly}
              disabled={isActing}
              onChange={(e) => { setOffset(0); setOpenOnly(e.target.checked); }}
            />
            Open issues only
          </label>
          <label style={{ marginRight: '8px' }}>Page size:</label>
          <select value={pageSize} disabled={isActing} onChange={(e) => setPageSize(parseInt(e.target.value, 10))}>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
          <DeveloperNavigation />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: '12px', marginBottom: '12px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>User email</label>
          <input value={userEmail} disabled={isActing} onChange={(e) => setUserEmail(e.target.value)} placeholder="user_email" style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Contract ID</label>
          <input value={contractId} disabled={isActing} onChange={(e) => setContractId(e.target.value)} placeholder="contract_id" style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Listing ID</label>
          <input value={listingId} disabled={isActing} onChange={(e) => setListingId(e.target.value)} placeholder="listing_id" style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Field</label>
          <input value={fieldName} disabled={isActing} onChange={(e) => setFieldName(e.target.value)} placeholder="field_name" style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Start Date</label>
          <input type="date" value={dateStart} disabled={isActing} onChange={(e) => setDateStart(e.target.value)} style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>End Date</label>
          <input type="date" value={dateEnd} disabled={isActing} onChange={(e) => setDateEnd(e.target.value)} style={{ width: '100%' }} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <button disabled={isActing} onClick={() => { setOffset(0); fetchRows(true); }}>Apply Filters</button>
        <button disabled={isActing} onClick={() => {
          const cleared = {
            userEmail: '',
            contractId: '',
            listingId: '',
            fieldName: '',
            dateStart: '',
            dateEnd: '',
          };
          setUserEmail('');
          setContractId('');
          setListingId('');
          setFieldName('');
          setDateStart('');
          setDateEnd('');
          setOffset(0);
          fetchRows(true, cleared);
        }}>Clear</button>
        <button disabled={isActing} onClick={() => fetchRows(false)}>Load More</button>
      </div>

      {loading && <LoadingSpinner />}
      {error && (
        <div style={{ color: 'red', marginBottom: '12px' }}>{error}</div>
      )}

      <div style={{ overflowX: 'auto', background: '#ffffff', border: '1px solid #e5e7eb' }}>
        <table style={{ width: 'max-content', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'auto' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc', position: 'sticky', top: 0, zIndex: 1 }}>
                actions
              </th>
              {COLUMN_ORDER.map((col) => (
                <th
                  key={col}
                  style={{
                    textAlign: 'left',
                    padding: '10px 12px',
                    borderBottom: '1px solid #e5e7eb',
                    background: '#f8fafc',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={row.id || idx} style={{ borderBottom: '1px solid #f1f5f9', height: '100px', maxHeight: '100px' }}>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', borderRight: '1px solid #f1f5f9', verticalAlign: 'top', minWidth: 220 }}>
                  {!row.resolution && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <button
                        onClick={() => resolveRow(row, 'keep_keyflo', 'Keep KeyFlo')}
                        disabled={isActing}
                        style={{ fontSize: '0.8rem', padding: '4px 10px', cursor: isActing ? 'wait' : 'pointer' }}
                      >
                        Keep KeyFlo
                      </button>
                      <button
                        onClick={() => resolveRow(row, 'use_skyslope', 'Keep SkySlope')}
                        disabled={isActing}
                        style={{ fontSize: '0.8rem', padding: '4px 10px', cursor: isActing ? 'wait' : 'pointer' }}
                      >
                        Keep SkySlope
                      </button>
                      <button
                        onClick={() => resolveRow(row, 'dismissed', 'Resolve')}
                        disabled={isActing}
                        style={{ fontSize: '0.8rem', padding: '4px 10px', cursor: isActing ? 'wait' : 'pointer' }}
                      >
                        Resolve
                      </button>
                    </div>
                  )}
                </td>
                {COLUMN_ORDER.map((col) => {
                  let val = row[col];
                  if (col === 'resolved_at' || col === 'created_at') {
                    val = val ? formatDateTime(val) : '';
                  }
                  if (val === null || typeof val === 'undefined') val = '';
                  return (
                    <td
                      key={col}
                      style={{
                        padding: '10px 12px',
                        borderBottom: '1px solid #f1f5f9',
                        borderRight: '1px solid #f1f5f9',
                        verticalAlign: 'top',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        minWidth: (col === 'skyslope_value' || col === 'keyflo_value' || col === 'field_name') ? 280 : 160,
                      }}
                    >
                      <div style={{ maxHeight: '80px', overflowY: 'auto' }}>
                        {String(val)}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={COLUMN_ORDER.length + 1} style={{ padding: '12px', textAlign: 'center' }}>No discrepancies found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
    </>
  );
}

export default DeveloperSkySlopeDiscrepancies;
